/**
 * Bảng tông màu và khoá dịch của màn license cục bộ (ADR-0058 mục 6.3, 8).
 *
 * Màu trạng thái **không** được quyết trong `src/mock/`: tầng đó bị xoá khi có
 * API thật, mà bảng tông phải sống lâu hơn nó.
 */
import type { BadgeVariant } from '../../components/ui';
import type { KhoaDich } from '../../i18n';
import type { DemNguocLicense, LoaiTepNhap, MucDung, TrangThaiCucBo } from './types';

/**
 * Tông trạng thái — thang **trạng thái**, cùng luật với `computed_status` ở màn
 * license nền tảng: `GRACE` là `danger` vì hết ân hạn là bản cài chuyển chỉ đọc
 * ngay; `NOT_YET_VALID` là `primary` (chưa tới lượt, không phải lỗi).
 */
export const TRANG_THAI_CUC_BO: Record<TrangThaiCucBo, { khoa: KhoaDich; variant: BadgeVariant }> = {
  ACTIVE: { khoa: 'licenseCucBo.trangThai.ACTIVE', variant: 'success' },
  EXPIRING: { khoa: 'licenseCucBo.trangThai.EXPIRING', variant: 'warning' },
  GRACE: { khoa: 'licenseCucBo.trangThai.GRACE', variant: 'danger' },
  READ_ONLY: { khoa: 'licenseCucBo.trangThai.READ_ONLY', variant: 'danger' },
  NOT_YET_VALID: { khoa: 'licenseCucBo.trangThai.NOT_YET_VALID', variant: 'primary' },
  INVALID: { khoa: 'licenseCucBo.trangThai.INVALID', variant: 'danger' },
};

/** Câu của băng cảnh báo theo trạng thái; `{{so}}` là số ngày. */
export const CAU_BANG_CANH_BAO: Record<Exclude<TrangThaiCucBo, 'ACTIVE'>, KhoaDich> = {
  EXPIRING: 'licenseCucBo.bang.EXPIRING',
  GRACE: 'licenseCucBo.bang.GRACE',
  READ_ONLY: 'licenseCucBo.bang.READ_ONLY',
  NOT_YET_VALID: 'licenseCucBo.bang.NOT_YET_VALID',
  INVALID: 'licenseCucBo.bang.INVALID',
};

/** Lớp của băng theo mức: nền và chữ cùng một bậc thang màu nhấn. */
export const LOP_BANG: Record<'warning' | 'danger', string> = {
  warning: 'border-warning-200 bg-warning-50 text-warning-700',
  danger: 'border-danger-200 bg-danger-50 text-danger-700',
};

/**
 * Nhãn các giới hạn đã biết. Khoá khác trong `limits` là **dữ liệu của license**
 * (nhà cung cấp đặt tên), hiện nguyên văn, không dịch.
 */
export const NHAN_GIOI_HAN: Record<string, KhoaDich> = {
  max_users: 'licenseCucBo.gioiHan.max_users',
  max_nodes: 'licenseCucBo.gioiHan.max_nodes',
  storage_gb: 'licenseCucBo.gioiHan.storage_gb',
};

/** Màu thanh mức dùng. */
export const LOP_THANH: Record<MucDung['muc'], string> = {
  'binh-thuong': 'bg-success-500',
  'sap-cham': 'bg-warning-500',
  'da-cham': 'bg-danger-500',
  'khong-ro': 'bg-content-muted',
};

export const NHAN_LOAI_TEP: Record<LoaiTepNhap, KhoaDich> = {
  license: 'licenseCucBo.loaiTep.license',
  keyring: 'licenseCucBo.loaiTep.keyring',
  revocation: 'licenseCucBo.loaiTep.revocation',
};

export const DA_NHAP: Record<LoaiTepNhap, KhoaDich> = {
  license: 'licenseCucBo.daNhap.license',
  keyring: 'licenseCucBo.daNhap.keyring',
  revocation: 'licenseCucBo.daNhap.revocation',
};

/** Câu dưới badge trạng thái; `{{so}}` là số ngày tới mốc kế. */
export const CAU_CON_LAI: Record<TrangThaiCucBo, KhoaDich> = {
  ACTIVE: 'licenseCucBo.conLai.ACTIVE',
  EXPIRING: 'licenseCucBo.conLai.EXPIRING',
  GRACE: 'licenseCucBo.conLai.GRACE',
  READ_ONLY: 'licenseCucBo.conLai.READ_ONLY',
  NOT_YET_VALID: 'licenseCucBo.conLai.NOT_YET_VALID',
  INVALID: 'licenseCucBo.conLai.INVALID',
};

/**
 * Dòng đếm ngược của thẻ license trên thanh điều hướng; `{{so}}` là số ngày.
 * `mot` là câu số ít cho đúng 1 ngày (tiếng Anh cần, tiếng Việt viết như nhau),
 * cùng cách `thungRac.conMotNgay` đang làm.
 */
export const CAU_DEM_NGUOC: Record<DemNguocLicense['loai'], { nhieu: KhoaDich; mot?: KhoaDich }> = {
  ACTIVE: { nhieu: 'licenseCucBo.thanhBen.conNgay', mot: 'licenseCucBo.thanhBen.conMotNgay' },
  EXPIRING: { nhieu: 'licenseCucBo.thanhBen.conNgay', mot: 'licenseCucBo.thanhBen.conMotNgay' },
  GRACE: { nhieu: 'licenseCucBo.thanhBen.anHanNgay', mot: 'licenseCucBo.thanhBen.anHanMotNgay' },
  NOT_YET_VALID: { nhieu: 'licenseCucBo.thanhBen.hieuLucSau', mot: 'licenseCucBo.thanhBen.hieuLucSauMotNgay' },
  READ_ONLY: { nhieu: 'licenseCucBo.thanhBen.chiDoc' },
  INVALID: { nhieu: 'licenseCucBo.thanhBen.khongHopLe' },
  CHUA_KICH_HOAT: { nhieu: 'licenseCucBo.thanhBen.chuaKichHoat' },
};
